import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const statusDotVariants = cva(
  "shrink-0 rounded-full",
  {
    variants: {
      status: {
        connected: "bg-daydra-success",
        disconnected: "bg-daydra-muted",
        error: "bg-daydra-error",
        warning: "bg-daydra-warning",
        busy: "bg-daydra-accent",
        idle: "bg-daydra-faint",
      },
      size: {
        sm: "h-1.5 w-1.5",
        default: "h-2 w-2",
        lg: "h-2.5 w-2.5",
      },
      pulse: {
        true: "animate-pulse-soft",
        false: "",
      },
    },
    defaultVariants: {
      status: "disconnected",
      size: "default",
      pulse: false,
    },
  },
);

export interface StatusDotProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusDotVariants> {
  /** Optional label shown next to the dot */
  label?: string;
  /** Label visibility on mobile */
  labelHidden?: boolean;
}

export function StatusDot({
  className,
  status,
  size,
  pulse,
  label,
  labelHidden = true,
  ...props
}: StatusDotProps) {
  if (!label) {
    return (
      <div
        className={cn(statusDotVariants({ status, size, pulse, className }))}
        {...props}
      />
    );
  }

  // Derive label color from status
  const labelColor = {
    connected: "text-daydra-success",
    disconnected: "text-daydra-muted",
    error: "text-daydra-error",
    warning: "text-daydra-warning",
    busy: "text-daydra-accent",
    idle: "text-daydra-faint",
  }[status ?? "disconnected"];

  return (
    <div className={cn("flex items-center gap-1.5", className)} {...props}>
      <div className={cn(statusDotVariants({ status, size, pulse }))} />
      <span
        className={cn(
          "text-2xs",
          labelColor,
          labelHidden && "hidden sm:inline",
        )}
      >
        {label}
      </span>
    </div>
  );
}
